import type { WebPageLoadEvent, WebPageLoadStatus, WebPageLoadType } from '@doss/common-all'

// "Network quiet" observes GraphQL operations only (via the Apollo timing link's in-flight
// counter). Data fetched outside Apollo — e.g. react-query or raw fetch — is invisible to the
// tracker, so pages loading that way complete early with small values.
const QUIET_WINDOW_MS = 100
// Quiet confirmation while a load has observed zero GraphQL operations. Lazy-loaded route chunks
// commonly take a few hundred ms to download, mount, and fire their first query; the short
// window would declare a premature near-0ms complete before any of that happens. Once an
// operation is observed for the load, QUIET_WINDOW_MS applies.
const NO_OPERATIONS_QUIET_WINDOW_MS = 500
const LOAD_TIMEOUT_MS = 60_000

export type PageLoadTrackerDeps = {
  now: () => number
  getInFlightOperationCount: () => number
  subscribeToInFlightOperations: (listener: (count: number) => void) => () => void
  setTimeout: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>
  clearTimeout: (timer: ReturnType<typeof setTimeout>) => void
  emit: (event: Omit<WebPageLoadEvent, 'ts'>) => void
}

export type PageLoadTracker = {
  start: () => void
  onRouteChange: (
    pageType: string,
    loadType: WebPageLoadType,
    options?: { startedAt?: number },
  ) => void
  // Host signal that the document is unloading (e.g. pagehide). An in-progress load emits
  // `abandoned` with its elapsed time, exactly once — abandoning finishes the load, so it can
  // never also emit complete/timeout/interrupted. No-op when nothing is loading.
  abandon: () => void
  cancel: () => void
  dispose: () => void
}

type ActiveLoad = {
  pageType: string
  loadType: WebPageLoadType
  startedAt: number
  // Whether GraphQL activity was observed after this load started. Selects between the two
  // quiet-window lengths without mistaking a previous route's request for new-route work.
  hasObservedOperation: boolean
  inFlightOperationCount: number
  quietEnteredAt?: number
  quietTimer?: ReturnType<typeof setTimeout>
  timeoutTimer?: ReturnType<typeof setTimeout>
}

const getQuietWindowMs = (load: ActiveLoad): number =>
  load.hasObservedOperation ? QUIET_WINDOW_MS : NO_OPERATIONS_QUIET_WINDOW_MS

export const createPageLoadTracker = (deps: PageLoadTrackerDeps): PageLoadTracker => {
  let active: ActiveLoad | undefined
  let unsubscribe: (() => void) | undefined

  const clearTimers = (load: ActiveLoad): void => {
    if (load.quietTimer !== undefined) {
      deps.clearTimeout(load.quietTimer)
      load.quietTimer = undefined
    }
    if (load.timeoutTimer !== undefined) {
      deps.clearTimeout(load.timeoutTimer)
      load.timeoutTimer = undefined
    }
  }

  const finish = (status: WebPageLoadStatus, valueMs: number): void => {
    if (!active) {
      return
    }
    const load = active
    active = undefined
    clearTimers(load)
    deps.emit({
      type: 'page_load',
      page_type: load.pageType,
      load_type: load.loadType,
      status,
      value_ms: Math.max(0, valueMs),
    })
  }

  const finishIfDeadlineElapsed = (now: number): boolean => {
    if (!active) {
      return false
    }
    const load = active
    const timeoutDeadline = load.startedAt + LOAD_TIMEOUT_MS
    const quietEnteredAt = load.quietEnteredAt
    const quietDeadline =
      load.inFlightOperationCount === 0 && quietEnteredAt !== undefined
        ? quietEnteredAt + getQuietWindowMs(load)
        : undefined

    // Browser scheduling can run timer callbacks late or out of deadline order. Resolve the
    // earliest eligible outcome before any later event mutates the active load.
    if (
      quietEnteredAt !== undefined &&
      quietDeadline !== undefined &&
      quietDeadline < timeoutDeadline &&
      now >= quietDeadline
    ) {
      finish('complete', quietEnteredAt - load.startedAt)
      return true
    }
    if (now >= timeoutDeadline) {
      finish('timeout', LOAD_TIMEOUT_MS)
      return true
    }
    return false
  }

  const startQuietWindow = (): void => {
    if (!active) {
      return
    }
    // A load with no observed operations may simply not have mounted its route chunk yet, so its
    // quiet confirmation waits longer before declaring the page complete.
    const windowMs = getQuietWindowMs(active)
    active.quietEnteredAt = deps.now()
    active.quietTimer = deps.setTimeout(() => {
      finishIfDeadlineElapsed(deps.now())
    }, windowMs)
  }

  const handleInFlightChange = (count: number): void => {
    if (!active || finishIfDeadlineElapsed(deps.now())) {
      return
    }
    const previousCount = active.inFlightOperationCount
    active.inFlightOperationCount = count
    if (count === 0) {
      startQuietWindow()
      return
    }
    if (count > previousCount) {
      active.hasObservedOperation = true
    }
    if (active.quietTimer !== undefined) {
      deps.clearTimeout(active.quietTimer)
      active.quietTimer = undefined
      active.quietEnteredAt = undefined
    }
  }

  const cancel = (): void => {
    if (active) {
      clearTimers(active)
      active = undefined
    }
  }

  return {
    start: () => {
      unsubscribe ??= deps.subscribeToInFlightOperations(handleInFlightChange)
    },
    onRouteChange: (pageType, loadType, options) => {
      const now = deps.now()
      if (active && !finishIfDeadlineElapsed(now)) {
        finish('interrupted', now - active.startedAt)
      }
      const startedAt = options?.startedAt ?? now
      const timeoutDelay = Math.max(0, LOAD_TIMEOUT_MS - (now - startedAt))
      const inFlightCount = deps.getInFlightOperationCount()
      active = {
        pageType,
        loadType,
        startedAt,
        // Work present during initial tracking belongs to the document load. On route changes,
        // baseline work may belong to the previous route, so only a later increase proves the new
        // load observed an operation.
        hasObservedOperation: loadType === 'initial' && inFlightCount > 0,
        inFlightOperationCount: inFlightCount,
      }
      active.timeoutTimer = deps.setTimeout(() => {
        finishIfDeadlineElapsed(deps.now())
      }, timeoutDelay)
      if (inFlightCount === 0) {
        startQuietWindow()
      }
    },
    abandon: () => {
      if (!active) {
        return
      }
      const now = deps.now()
      if (finishIfDeadlineElapsed(now)) {
        return
      }
      finish('abandoned', now - active.startedAt)
    },
    cancel,
    dispose: () => {
      cancel()
      unsubscribe?.()
      unsubscribe = undefined
    },
  }
}
